import React, { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { DEAL_OPTIONS, PLATFORM_LABEL, TYPE_OPTIONS, formatFcfa, formatMillions } from './labels';
import apiService from '../../api';

// Feed filters an alert keeps (FILTER_KEYS in backend/Hokue/listings/alerts.py); the sort doesn't matter.
const ALERT_KEYS = ['deal', 'type', 'city', 'q', 'price_max', 'new', 'platform', 'agent'];

export const alertFilters = (filters) =>
  Object.fromEntries(ALERT_KEYS.filter((k) => filters[k]).map((k) => [k, filters[k]]));

// The criteria in words, as the emails write them (alerts.describe in the backend).
export function describeFilters(filters, agents = []) {
  const f = filters;
  const parts = [];
  const deal = DEAL_OPTIONS.find(([v]) => v && v === f.deal);
  if (deal) parts.push(deal[1]);
  const type = TYPE_OPTIONS.find(([v]) => v === f.type);
  if (type) parts.push(type[1]);
  if (f.city) parts.push(f.city);
  if (f.q) parts.push(`« ${f.q} »`);
  if (f.price_max) {
    const v = Number(f.price_max);
    parts.push(`jusqu'à ${f.deal === 'sale' ? formatMillions(v) : `${formatFcfa(v)}/mois`}`);
  }
  if (f.new === '1') parts.push('Nouvelle construction');
  const agent = agents.find((a) => String(a.id) === String(f.agent));
  if (agent) parts.push(`Agent : ${agent.name}`);
  if (f.platform) parts.push(`Sur ${PLATFORM_LABEL[f.platform]}`);
  return parts.join(' · ') || 'Toutes les annonces';
}

// "Créer une alerte": the current filters, an email address, and a confirmation email.
const AlertForm = ({ country, filters, agents, onClose }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('form'); // form | sending | sent | error | throttled
  const criteria = describeFilters(filters, agents);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await apiService.post('/api/v1/listings/alerts/', { email: email.trim(), country, filters: alertFilters(filters) });
      setStatus('sent');
    } catch (err) {
      setStatus(err?.response?.status === 429 ? 'throttled' : 'error');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Créer une alerte"
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-bold text-gray-900 inline-flex items-center gap-2"><Bell size={20} className="text-blue-600" /> Alerte e-mail</h2>
          <button type="button" onClick={onClose} aria-label="Fermer" className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {status === 'sent' ? (
          <div className="mt-4 text-sm text-gray-700 space-y-3">
            <p className="font-semibold text-gray-900">Vérifiez votre boîte mail.</p>
            <p>Nous avons envoyé un lien à <strong>{email.trim()}</strong>. Cliquez dessus pour activer l'alerte (pensez aux spams).</p>
            <button type="button" onClick={onClose} className="w-full bg-blue-600 text-white font-semibold rounded-lg py-2.5">OK</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <p className="text-sm text-gray-600">Recevez par e-mail les nouvelles annonces qui correspondent à :</p>
            <p className="text-sm font-semibold text-gray-900 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">{criteria}</p>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre adresse e-mail"
              autoComplete="email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {status === 'error' && <p className="text-sm text-red-600">Impossible de créer l'alerte. Vérifiez l'adresse et réessayez.</p>}
            {status === 'throttled' && <p className="text-sm text-red-600">Trop de demandes. Réessayez dans une heure.</p>}
            <button type="submit" disabled={status === 'sending'} className="w-full bg-blue-600 text-white font-semibold rounded-lg py-2.5 disabled:opacity-50">
              {status === 'sending' ? 'Envoi…' : 'Créer mon alerte'}
            </button>
            <p className="text-xs text-gray-500">
              Une alerte par adresse : une nouvelle alerte remplace la précédente. Chaque e-mail contient un bouton pour la désactiver.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default AlertForm;
